import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { Send } from 'lucide-react';
import { X, Mail, Lock, User, AlertCircle, CheckCircle, Eye, EyeOff, KeyRound } from 'lucide-react';

const API_BASE = "/api";

function AuthModal({ isOpen, onClose, mode: initialMode = 'signin', onSuccess, darkMode = false }) {
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationData, setVerificationData] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [telegramAuth, setTelegramAuth] = useState(null);
  const [telegramStatus, setTelegramStatus] = useState('pending');
  const [telegramStarting, setTelegramStarting] = useState(false);

  const greenGradientStart = '#7BC00F';
  const greenGradientMiddle = '#95DE1A';
  const greenGradientEnd = '#A8F02B';
  const redColor = '#FD0E0B';

  useEffect(() => {
    if (!isOpen || mode !== 'telegram-auth' || !telegramAuth?.token) return;

    let active = true;
    let timer;

    const checkStatus = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/telegram-auth/status/${telegramAuth.token}`
        );
        const data = await response.json();

        if (!active) return;

        if (response.ok && data.status === 'authenticated') {
          setTelegramStatus('authenticated');
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          localStorage.setItem('role', data.user.role);
          onSuccess(data.user);
          return;
        }

        if (response.status === 410) {
          setTelegramStatus('expired');
          setError(data.error || 'Telegram oturumunun süresi doldu.');
          return;
        }

        if (!response.ok && response.status !== 409) {
          setError(data.error || 'Telegram doğrulaması kontrol edilemedi.');
        }
      } catch {
        if (active) setError('Bağlantı hatası. Yeniden deneniyor...');
      }

      if (active) timer = setTimeout(checkStatus, 5000);
    };

    checkStatus();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [isOpen, mode, telegramAuth?.token, onSuccess]);

  // Modal açıldığında veya initialMode değiştiğinde modu ve formu güncelle
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      resetForm();
    }
  }, [initialMode, isOpen]);

  if (!isOpen) return null;

  // ---- Validasyonlar ----
  const validateName = (name, fieldName) => {
    if (name.trim() === '') return '';
    if (/^\d+$/.test(name)) return `${fieldName} cannot consist of only numbers`;
    if (!/^[a-zA-Z\s'-]+$/.test(name)) return `${fieldName} can only contain letters, spaces, apostrophes and hyphens`;
    return '';
  };

  const validatePassword = (password) => {
    if (password.length === 0) return '';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    return '';
  };

  const validateConfirmPassword = (confirmPwd) => {
    if (confirmPwd.length === 0) return '';
    if (confirmPwd !== formData.password) return 'Passwords do not match';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    let errorMsg = '';
    if (name === 'firstName' || name === 'lastName') {
      const fieldLabel = name === 'firstName' ? 'First name' : 'Last name';
      errorMsg = validateName(value, fieldLabel);
    } else if (name === 'password') {
      errorMsg = validatePassword(value);
      if (formData.confirmPassword) {
        const confirmError = validateConfirmPassword(formData.confirmPassword);
        setFieldErrors(prev => ({ ...prev, confirmPassword: confirmError }));
      }
    } else if (name === 'confirmPassword') {
      errorMsg = validateConfirmPassword(value);
    }

    setFieldErrors(prev => ({ ...prev, [name]: errorMsg }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email.includes('@')) errors.email = 'Please enter a valid email address';

    if (mode === 'signup') {
      const firstNameError = validateName(formData.firstName, 'First name');
      if (firstNameError) errors.firstName = firstNameError;
      const lastNameError = validateName(formData.lastName, 'Last name');
      if (lastNameError) errors.lastName = lastNameError;
      const pwdError = validatePassword(formData.password);
      if (pwdError) errors.password = pwdError;
      const confirmError = validateConfirmPassword(formData.confirmPassword);
      if (confirmError) errors.confirmPassword = confirmError;
    } else if (mode === 'signin') {
      if (!formData.email) errors.email = 'Email is required';
      if (!formData.password) errors.password = 'Password is required';
    } else if (mode === 'forgot') {
      if (!formData.email) errors.email = 'Email is required';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ---- API çağrıları ----
  const handleSendVerificationCode = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE}/auth/send-verification-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName
        })
      });
      const data = await response.json();
      if (response.ok) {
        setVerificationData({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName
        });
        setSuccess(`Verification code sent to ${formData.email}`);
        setMode('verify');
        setVerificationCode('');
      } else {
        setError(data.error || 'Failed to send code');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE}/auth/verify-and-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: verificationData.email,
          code: verificationCode
        })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem("role", data.user.role);
        onSuccess(data.user);
      } else {
        setError(data.error || 'Verification failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!verificationData) return;
    setIsResending(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE}/auth/send-verification-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: verificationData.email,
          password: verificationData.password,
          firstName: verificationData.firstName,
          lastName: verificationData.lastName
        })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(`New code sent to ${verificationData.email}`);
      } else {
        setError(data.error || 'Failed to resend');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setIsResending(false);
    }
  };

  const handleSignIn = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem("role", data.user.role);
        onSuccess(data.user);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess('Password reset link sent to your email');
        setFormData({ ...formData, email: '' });
      } else {
        setError(data.error || 'Failed to send reset email');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const startTelegramAuth = async (flow) => {
    setError('');
    setSuccess('');

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Lütfen geçerli bir e-posta adresi girin.');
      return;
    }

    if (flow === 'signup' && !validateForm()) return;

    setTelegramStarting(true);

    try {
      const response = await fetch(`${API_BASE}/telegram-auth/${flow}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          flow === 'signup'
            ? {
                email: formData.email,
                password: formData.password,
                firstName: formData.firstName,
                lastName: formData.lastName
              }
            : { email: formData.email }
        )
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Telegram işlemi başlatılamadı.');
        return;
      }

      setTelegramAuth({
        token: data.token,
        link: data.link,
        flow
      });
      setTelegramStatus('pending');
      setMode('telegram-auth');
    } catch {
      setError('Sunucuya bağlanılamadı.');
    } finally {
      setTelegramStarting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === 'signin') handleSignIn();
    else if (mode === 'signup') handleSendVerificationCode();
    else if (mode === 'forgot') handleForgotPassword();
    else if (mode === 'verify') handleVerifyAndRegister();
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', firstName: '', lastName: '', confirmPassword: '' });
    setVerificationCode('');
    setVerificationData(null);
    setTelegramAuth(null);
    setTelegramStatus('pending');
    setError('');
    setSuccess('');
    setFieldErrors({});
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    resetForm();
  };

  if (mode === 'telegram-auth' && telegramAuth) {
    return (
      <div className={`fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4 ${darkMode ? 'bg-black/50' : 'bg-red-900/30'}`}>
        <div className={`rounded-3xl shadow-2xl w-full max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={`flex items-center justify-between p-8 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <div>
              <h2 className="text-2xl font-bold" style={{ color: redColor }}>
                Telegram {telegramAuth.flow === 'signup' ? 'Registration' : 'Login'}
              </h2>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                QR kodu okutun veya Telegram'da açın.
              </p>
            </div>
            <button onClick={onClose} className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-8 space-y-5 text-center">
            {error && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-2xl">
                <QRCode value={telegramAuth.link} size={208} />
              </div>
            </div>

            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {telegramAuth.flow === 'signup'
                ? 'Kaydı tamamlamak için Telegram botunda telefon numaranızı paylaşın.'
                : 'Telegram botunda giriş isteğini onaylayın.'}
            </p>

            <a
              href={telegramAuth.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full text-white font-semibold px-6 py-3 rounded-2xl"
              style={{ backgroundColor: redColor }}
            >
              <Send className="h-5 w-5" />
              Telegram'da Aç
            </a>

            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {telegramStatus === 'pending' && 'Telegram onayı bekleniyor...'}
              {telegramStatus === 'processing' && 'İşlem tamamlanıyor...'}
              {telegramStatus === 'expired' && 'Oturum süresi doldu. Yeniden deneyin.'}
            </p>

            <button
              type="button"
              onClick={() => {
                const previousMode = telegramAuth.flow === 'signup' ? 'signup' : 'signin';
                setTelegramAuth(null);
                setTelegramStatus('pending');
                setError('');
                setMode(previousMode);
              }}
              className="text-sm font-medium hover:underline"
              style={{ color: redColor }}
            >
              Geri dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Verify ekranı (darkMode destekli) ----
  if (mode === 'verify') {
    return (
      <div className={`fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4 ${darkMode ? 'bg-black/50' : 'bg-red-900/30'}`}>
        <div className={`rounded-3xl shadow-2xl w-full max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={`flex items-center justify-between p-8 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <div>
              <h2 className="text-2xl font-bold" style={{ color: redColor }}>Verify Your Email</h2>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                We sent a 6‑digit code to<br />
                <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{verificationData?.email}</span>
              </p>
            </div>
            <button onClick={onClose} className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <X className={`h-5 w-5 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {error && (
              <div className={`flex items-center space-x-3 p-4 rounded-2xl ${darkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className={`text-sm font-medium ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</span>
              </div>
            )}
            {success && (
              <div className={`flex items-center space-x-3 p-4 border rounded-2xl ${darkMode ? 'bg-gray-900' : 'bg-white'}`} style={{ borderColor: '#7BC00F' }}>
                <CheckCircle className="h-5 w-5" style={{ color: '#7BC00F' }} />
                <span className="text-sm font-medium" style={{ color: '#7BC00F' }}>{success}</span>
              </div>
            )}
            <div className="space-y-2">
              <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Verification Code *</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(val);
                    if (error) setError('');
                  }}
                  placeholder="123456"
                  maxLength="6"
                  className={`w-full pl-12 pr-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FD0E0B] border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                  required
                />
              </div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Enter the 6‑digit code sent to your email</p>
            </div>
            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="w-full text-white font-medium px-6 py-3 rounded-2xl shadow-lg transition-all hover:shadow-xl disabled:opacity-50"
              style={{ background: `linear-gradient(135deg, ${greenGradientStart} 0%, ${greenGradientMiddle} 50%, ${greenGradientEnd} 100%)` }}
            >
              {loading ? <div className="flex items-center justify-center space-x-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>Verifying...</span></div> : 'Verify & Register'}
            </button>
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={handleResendCode} disabled={isResending} className="text-sm font-medium hover:underline disabled:opacity-50" style={{ color: redColor }}>{isResending ? 'Sending...' : 'Resend code'}</button>
              <button type="button" onClick={() => switchMode('signup')} className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'}`}>← Back to sign up</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ---- Ana form (signin, signup, forgot) darkMode destekli ----
  return (
    <div className={`fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4 ${darkMode ? 'bg-black/50' : 'bg-red-900/30'}`}>
      <div className={`rounded-3xl shadow-2xl w-full max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`flex items-center justify-between p-8 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <div>
            <h2 className="text-2xl font-bold" style={{ color: redColor }}>
              {mode === 'signin' && 'Welcome Back'}
              {mode === 'signup' && 'Create Account'}
              {mode === 'forgot' && 'Reset Password'}
            </h2>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {mode === 'signin' && 'Sign in to your account'}
              {mode === 'signup' && 'Start monitoring your websites'}
              {mode === 'forgot' && 'Enter your email to reset password'}
            </p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
            <X className={`h-5 w-5 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {error && (
            <div className={`flex items-center space-x-3 p-4 rounded-2xl ${darkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
              <AlertCircle className="h-5 w-5 text-red-500" />
              <span className={`text-sm font-medium ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</span>
            </div>
          )}
          {success && (
            <div className={`flex items-center space-x-3 p-4 border rounded-2xl ${darkMode ? 'bg-gray-900' : 'bg-white'}`} style={{ borderColor: '#7BC00F' }}>
              <CheckCircle className="h-5 w-5" style={{ color: '#7BC00F' }} />
              <span className="text-sm font-medium" style={{ color: '#7BC00F' }}>{success}</span>
            </div>
          )}

          {mode === 'signup' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>First Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    className={`w-full pl-12 pr-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FD0E0B] border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} ${fieldErrors.firstName ? 'border-red-500' : ''}`}
                    required
                  />
                </div>
                {fieldErrors.firstName && <p className="text-xs text-red-500 mt-1">{fieldErrors.firstName}</p>}
              </div>
              <div className="space-y-2">
                <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Last Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Doe"
                    className={`w-full pl-12 pr-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FD0E0B] border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} ${fieldErrors.lastName ? 'border-red-500' : ''}`}
                    required
                  />
                </div>
                {fieldErrors.lastName && <p className="text-xs text-red-500 mt-1">{fieldErrors.lastName}</p>}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email Address *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                className={`w-full pl-12 pr-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FD0E0B] border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} ${fieldErrors.email ? 'border-red-500' : ''}`}
                required
              />
            </div>
            {fieldErrors.email && <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>}
          </div>

          {mode !== 'forgot' && (
            <>
              <div className="space-y-2">
                <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className={`w-full pl-12 pr-12 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FD0E0B] border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} ${fieldErrors.password ? 'border-red-500' : ''}`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {fieldErrors.password && <p className="text-xs text-red-500 mt-1">{fieldErrors.password}</p>}
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Password must be at least 8 characters and include uppercase, lowercase, and a number.
                </p>
              </div>

              {mode === 'signup' && (
                <div className="space-y-2">
                  <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Confirm Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className={`w-full pl-12 pr-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FD0E0B] border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} ${fieldErrors.confirmPassword ? 'border-red-500' : ''}`}
                      required
                    />
                  </div>
                  {fieldErrors.confirmPassword && <p className="text-xs text-red-500 mt-1">{fieldErrors.confirmPassword}</p>}
                </div>
              )}
            </>
          )}

          {mode === 'signin' && (
            <button
              type="button"
              disabled={telegramStarting}
              onClick={() => startTelegramAuth('login')}
              className="w-full flex items-center justify-center gap-2 text-white font-semibold px-6 py-3 rounded-2xl disabled:opacity-50"
              style={{ backgroundColor: redColor }}
            >
              <Send className="h-5 w-5" />
              {telegramStarting ? 'Connecting...' : 'Continue with Telegram'}
            </button>
          )}

          {mode === 'signup' && (
            <button
              type="button"
              disabled={telegramStarting}
              onClick={() => startTelegramAuth('signup')}
              className="w-full flex items-center justify-center gap-2 text-white font-semibold px-6 py-3 rounded-2xl disabled:opacity-50"
              style={{ backgroundColor: redColor }}
            >
              <Send className="h-5 w-5" />
              {telegramStarting ? 'Connecting...' : 'Register with Telegram'}
            </button>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full text-white font-medium px-6 py-3 rounded-2xl shadow-lg transition-all hover:shadow-xl disabled:opacity-50"
            style={{ background: `linear-gradient(135deg, ${greenGradientStart} 0%, ${greenGradientMiddle} 50%, ${greenGradientEnd} 100%)` }}
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>
                  {mode === 'signin' && 'Signing In...'}
                  {mode === 'signup' && 'Sending Code...'}
                  {mode === 'forgot' && 'Sending Email...'}
                </span>
              </div>
            ) : (
              <>
                {mode === 'signin' && 'Sign In'}
                {mode === 'signup' && 'Create Account'}
                {mode === 'forgot' && 'Send Reset Link'}
              </>
            )}
          </button>

          <div className="text-center space-y-4">
            {mode === 'signin' && (
              <>
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="w-full text-white font-medium px-4 py-2 rounded-xl shadow-md transition-all hover:shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${greenGradientStart} 0%, ${greenGradientMiddle} 50%, ${greenGradientEnd} 100%)` }}
                >
                  Forgot your password?
                </button>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Don't have an account?{' '}
                  <button type="button" onClick={() => switchMode('signup')} className="font-medium hover:underline" style={{ color: redColor }}>
                    Sign up
                  </button>
                </p>
              </>
            )}
            {mode === 'signup' && (
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Already have an account?{' '}
                <button type="button" onClick={() => switchMode('signin')} className="font-medium hover:underline" style={{ color: redColor }}>
                  Sign in
                </button>
              </p>
            )}
            {mode === 'forgot' && (
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Remember your password?{' '}
                <button type="button" onClick={() => switchMode('signin')} className="font-medium hover:underline" style={{ color: redColor }}>
                  Sign in
                </button>
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default AuthModal;